import React from 'react';

import type { MaybeMasked, OperationVariables, QueryFunctionOptions } from '@apollo/client';
import { isFunction, omit } from 'es-toolkit';
import { has } from 'es-toolkit/compat';
import { useDebounce } from 'use-debounce';

import { mergeObjects } from '@hrlogics/helper-utils';
import type { GeneratedQueryHookType } from '@hrlogics/react-apollo-v3';

import { DEFAULT_DEBOUNCE_DELAY, DEFAULT_NUMBER_OF_SEARCH_OPTIONS } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import type {
  AutocompleteFieldDataFetchParams,
  AutocompleteFieldRenderParams,
} from 'src/types/components';

export type AutocompleteFieldResult<Option> = AutocompleteFieldRenderParams<Option> &
  AutocompleteFieldDataFetchParams<Option>;

type GetQueryItemsFunc<Query, Option> = (
  queryData: MaybeMasked<Query> | undefined,
) => Nullable<Option[]>;

type SkipQueryOption = boolean | ((searchText: string) => boolean);

export type QueryParamsVariables<
  Query = any,
  Variables extends OperationVariables = OperationVariables,
> =
  | {
      getCustomQueryVariables?: (searchText: string) => Variables;
      queryFilter?: never;
      queryOptions?: never;
    }
  | {
      getCustomQueryVariables?: never;
      queryFilter?: (searchText: string) => Variables;
      queryOptions?: Omit<QueryFunctionOptions<Query, Variables>, 'skip'> & {
        skip?: SkipQueryOption;
      };
    };

type QueryParams<Option, Query = any, Variables extends OperationVariables = OperationVariables> = {
  getQueryItems: GetQueryItemsFunc<Query, Option>;
} & QueryParamsVariables<Query, Variables>;

export const useAutocompleteFieldWithQuery = <
  Option = any,
  Query = any,
  Variables extends OperationVariables = OperationVariables,
>(
  useQuery: GeneratedQueryHookType<Query, Variables>,
  queryParams: QueryParams<Option, Query, Variables>,
  renderParams: AutocompleteFieldRenderParams<Option>,
): AutocompleteFieldResult<Option> => {
  const [searchText, setSearchText] = React.useState<string>('');
  const [searchTextValue] = useDebounce(searchText, DEFAULT_DEBOUNCE_DELAY);

  const variables = React.useMemo(() => {
    if (isFunction(queryParams.getCustomQueryVariables)) {
      return queryParams.getCustomQueryVariables(searchTextValue);
    }

    return {
      ...queryParams.queryFilter?.(searchTextValue),
      first: DEFAULT_NUMBER_OF_SEARCH_OPTIONS,
    };
  }, [queryParams, searchTextValue]);

  const resolvedQueryOptions = React.useMemo(() => {
    const { skip: skipRaw } = queryParams.queryOptions || {};
    const skip = isFunction(skipRaw) ? skipRaw(searchTextValue) : skipRaw;

    return {
      ...omit(queryParams.queryOptions || {}, ['skip']),
      skip,
    };
  }, [queryParams.queryOptions, searchTextValue]);

  const mergedQueryOptions = React.useMemo(() => {
    return mergeObjects<any>({ variables }, resolvedQueryOptions);
  }, [resolvedQueryOptions, variables]);

  const { data, loading } = useQuery({
    ...mergedQueryOptions,
    fetchPolicy: 'network-only',
  });

  const onSearch = React.useCallback((searchText: string) => {
    setSearchText(searchText);
  }, []);

  const options = React.useMemo(() => {
    return queryParams.getQueryItems(data) || [];
  }, [data, queryParams]);

  // handle common cases
  const optionKey = React.useMemo(() => {
    const option = options[0];
    if (has(option, 'id')) return 'id';
    if (has(option, 'value')) return 'value';
    return undefined;
  }, [options]);

  return {
    ...renderParams,
    options,
    optionKey,
    onSearch,
    loading,
  };
};
